import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Car, CheckCircle2, Flag, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import NotFound from "@/pages/NotFound";
import VinLookupCard from "@/components/sections/VinLookupCard";
import TrustBadges from "@/components/sections/TrustBadges";
import FaqAccordion from "@/components/sections/FaqAccordion";
import { CITY_PAGES } from "@/lib/cityPages";
import { BUSINESS } from "@/lib/constants";
import { cityPageSchema } from "@/lib/structuredData";

const STEPS = (city) => [
  { Icon: Car, t: "Request an offer online", d: `Enter your VIN or plate, mileage and condition. Takes two minutes. It is a request, not a purchase offer — nothing is binding.` },
  { Icon: MapPin, t: `We come to you in ${city.name}`, d: `We call or text to confirm details, then drive out for a 15–30 minute inspection at your home or office. ${city.driveNote}` },
  { Icon: Clock, t: "Written offer, same-day payment", d: "You get a real offer in writing on the spot. Accept, and we complete the California title paperwork together and pay you the same day." },
];

export default function CityPage() {
  const { slug } = useParams();
  const city = CITY_PAGES[slug];
  if (!city) return <NotFound />;
  const url = `https://carforniabuyers.com/sell-my-car/${city.slug}`;

  return (
    <div data-testid={`page-city-${city.slug}`}>
      <SEO title={city.title} description={city.description} canonical={url} jsonLd={cityPageSchema(city)} />

      <section className="relative overflow-hidden bg-brand-navy text-brand-warm" data-testid="city-hero">
        <div className="absolute inset-0 bg-grain opacity-30" />
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-brand-blue/20 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 lg:pt-32 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <nav aria-label="Breadcrumb" className="text-xs text-blue-200/70 reveal reveal-1" data-testid="city-breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link> <span className="mx-1.5">/</span>
              <Link to="/service-areas" className="hover:text-white">Service Areas</Link> <span className="mx-1.5">/</span>
              <span className="text-white">{city.name}</span>
            </nav>
            <div className="flex flex-wrap items-center gap-2 mt-5 reveal reveal-1">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-[0.2em]"><Flag className="w-3.5 h-3.5 text-brand-orange" /> Veteran-owned · Hayward, CA</span>
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-bold uppercase tracking-[0.2em]"><MapPin className="w-3.5 h-3.5 text-brand-orange" /> Serving {city.name} · {city.county}</span>
            </div>
            <h1 className="font-display font-extrabold tracking-[-0.03em] leading-[0.98] text-[clamp(2.4rem,4.6vw,4.5rem)] mt-6 reveal reveal-2" data-testid="city-headline">
              {city.headline[0]}<br /><span className="text-brand-blue brightness-150">{city.headline[1]}</span><br />{city.headline[2]}
            </h1>
            <p className="mt-7 text-lg text-blue-100/85 leading-relaxed max-w-xl reveal reveal-3" data-testid="city-intro">{city.intro}</p>
            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-lg border-t border-white/15 pt-6 reveal reveal-4">
              {[["3,000+", "cars bought"], ["25+ years", "in the business"], ["Same-day", "payment"]].map(([v, l]) => (
                <div key={l}><dt className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{v}</dt><dd className="text-[11px] uppercase tracking-[0.2em] text-blue-200/70 font-bold mt-1">{l}</dd></div>
              ))}
            </dl>
            <p className="mt-8 text-sm text-blue-200/70 reveal reveal-4">
              Prefer to talk? <a href={BUSINESS.phoneHref} className="text-brand-warm font-semibold hover:underline">Call</a> or <a href={BUSINESS.smsHref} className="text-brand-warm font-semibold hover:underline">text</a> {BUSINESS.phone}
            </p>
          </div>
          <div className="lg:col-span-6 w-full reveal reveal-3"><VinLookupCard /></div>
        </div>
      </section>

      <section className="relative -mt-10 lg:-mt-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10"><TrustBadges /></section>

      <section className="py-20 lg:py-28 bg-brand-surface" data-testid="city-how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">How it works in {city.name}</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mt-3 max-w-3xl">Three steps. No driving to a dealership.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            {STEPS(city).map((s, i) => (
              <div key={s.t} className="bg-white rounded-2xl p-7 border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`city-step-${i + 1}`}>
                <div className="flex items-center justify-between"><span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 text-brand-blue"><s.Icon className="w-5 h-5" /></span><span className="font-display text-3xl font-extrabold text-slate-200">0{i + 1}</span></div>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-5">{s.t}</h3>
                <p className="text-slate-600 mt-2 leading-relaxed text-[15px]">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white" data-testid="city-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Why {city.name} sellers choose us</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-3">A real buyer, not a lead form that gets sold to five dealers.</h2>
            <ul className="mt-8 space-y-4">
              {city.whyPoints.map((p) => (
                <li key={p} className="flex gap-3 text-slate-700 leading-relaxed"><CheckCircle2 className="w-5 h-5 text-brand-orange flex-shrink-0 mt-0.5" /><span>{p}</span></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl border border-slate-100 bg-brand-surface p-7 sm:p-9" data-testid="city-nearby">
              <div className="flex items-center gap-2 text-brand-blue"><MapPin className="w-5 h-5" /><span className="text-xs font-bold uppercase tracking-[0.2em]">Where we come to you</span></div>
              <h3 className="font-display text-2xl font-extrabold text-slate-900 mt-3">{city.name} and nearby</h3>
              <p className="text-slate-600 mt-2 text-[15px]">We inspect at your home or workplace anywhere in and around {city.name}, including:</p>
              <div className="flex flex-wrap gap-2 mt-5">
                {city.nearby.map((n) => <span key={n} className="px-3 py-1.5 rounded-full bg-white border border-slate-200 text-sm font-semibold text-slate-700">{n}</span>)}
              </div>
              <p className="text-xs text-slate-500 mt-5">Somewhere else on the {city.region}? <Link to="/service-areas" className="text-brand-blue font-semibold hover:underline">See all service areas</Link>.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-brand-surface" data-testid="city-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">{city.name} questions</p>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-3">Selling a car in {city.name}, answered.</h2>
          <div className="mt-10"><FaqAccordion items={city.faqs} testId="city-faq-accordion" /></div>
          <div className="mt-12 rounded-3xl bg-brand-navy text-brand-warm p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6" data-testid="city-cta">
            <div>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Ready to sell your car in {city.name}?</h3>
              <p className="text-blue-100/80 mt-2">Two-minute request. We come to you. Paid the same day you accept.</p>
            </div>
            <Link to="/sell-your-car"><Button data-testid="city-cta-button" className="h-13 px-8 bg-[#FF6B00] hover:bg-[#E66000] text-white font-bold rounded-xl text-base shadow-lg shadow-orange-500/30">Request my offer <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
